// Where a floating panel goes, beside the element it belongs to.
//
// Tooltips and help popovers both render in the browser's top layer — the
// popover attribute puts them there, which is what lets a tooltip inside the
// help sheet draw over the sheet rather than under it — and the top layer has
// no notion of "next to that button". Something has to say where, and it is
// this: one function for both, so the two cannot disagree about how close
// "beside" is or which way to flip when the edge of the window is in the way.
//
// Pure. It is handed rectangles and a viewport and answers with coordinates, so
// every flip and clamp can be tested without a browser laying anything out.

// Between the anchor's edge and the panel's.
export const GAP = 6
// The least the panel keeps from the edge of the window.
export const MARGIN = 8

export function anchorPosition(anchor, panel, viewport, { side = "top", align = "center", gap = GAP } = {}) {
  const roomAbove = anchor.top - gap - MARGIN
  const roomBelow = viewport.height - anchor.bottom - gap - MARGIN

  // Flip only where the other side is actually better: a panel that fits on
  // neither side stays where it was asked to be, clamped below.
  let placed = side
  if (side === "top" && panel.height > roomAbove && roomBelow > roomAbove) placed = "bottom"
  if (side === "bottom" && panel.height > roomBelow && roomAbove > roomBelow) placed = "top"

  const wanted = placed === "top" ? anchor.top - gap - panel.height : anchor.bottom + gap
  const top = Math.max(MARGIN, Math.min(wanted, viewport.height - MARGIN - panel.height))

  const start = align === "center" ? anchor.left + anchor.width / 2 - panel.width / 2 : anchor.left
  const left = Math.max(MARGIN, Math.min(start, viewport.width - MARGIN - panel.width))

  return { top: Math.round(top), left: Math.round(left), side: placed }
}

// Applies it to a live panel. Reads geometry, so the panel has to be displayed
// — shown as a popover, or otherwise laid out — before this is called.
export function place(panel, anchor, options) {
  const position = anchorPosition(
    anchor.getBoundingClientRect(),
    panel.getBoundingClientRect(),
    { width: window.innerWidth, height: window.innerHeight },
    options,
  )
  panel.style.top = `${position.top}px`
  panel.style.left = `${position.left}px`
  // Which way it went, so a stylesheet can point an arrow or round a corner.
  panel.dataset.side = position.side
  return position
}
